import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
    CreateRoyaltyManagerExecutionRequestInput,
    CreateRoyaltyManagerExecutionRequestOutput,
    DeleteRoyaltyManagerExecutionRequestsInput,
    GetRoyaltyManagerExecutionRequestDetailsByIdInput,
    GetRoyaltyManagerExecutionRequestsInput,
    GetRoyaltyManagerExecutionRequestsOutput,
    UpdateRoyaltyManagerExecutionRequestInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { ColDef } from 'ag-grid-community';
import { orderBy } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { ConfirmationModal } from 'src/components/ConfirmationModal/ConfirmationModal';
import { EXCEL_DATE_STYLE_ID } from 'src/constants/agGrid';
import { EDITOR_ROLE_SET } from 'src/constants/role';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IRoyaltyCalculationRequest } from 'src/types/IRoyaltyCalculationRequest';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import { getYYYYMMDDUTCStrFromTimestamp } from 'src/util/grid/cellGetter';
import { dateFilterOptions } from 'src/util/grid/filter';
import { dateComparator, dateFilterComparator } from 'src/util/grid/sort';

import { BaseAgGrid } from '../BaseAgGrid';
import { FilterInput } from '../FilterInput';
import { CreateRoyaltyManagerRequestModal } from './CreateRoyaltyManagerRequestModal';
import './RoyaltyManager.scss';
import { RoyaltyManagerRequestDetailsModal } from './RoyaltyManagerRequestDetailsModal';
import { getActionsCellRenderer, getBooleanCellRenderer, getStatusCellRenderer } from './utils';

export const RoyaltyManager: React.FC = () => {
    const [agGridApi, setAgGridApi] = useState<any>();
    const { setTopAlertMsg, setToasts, userRole, userInfo } = useContext(AppContext);
    const [requestsRowData, setRequestsRowData] = useState<IRoyaltyCalculationRequest[]>([]);
    const [requestsDataLoaded, setRequestsDataLoaded] = useState<boolean>(false);
    const [filterText, setFilterText] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<IRoyaltyCalculationRequest | null>(null);
    const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [requestToDelete, setRequestToDelete] = useState<IRoyaltyCalculationRequest | null>(null);

    const { pathname } = useLocation();
    const history = useHistory();

    useEffect(() => {
        if (pathname !== ROUTES.ROYALTY_MANAGER.INDEX) return;

        // Load royalty calculation requests from API
        setRequestsDataLoaded(false);

        // Call the GetRoyaltyManagerExecutionRequests API
        contractConfigurationClient
            .getRoyaltyManagerExecutionRequests(GetRoyaltyManagerExecutionRequestsInput.clone({}))
            .subscribe(
                (response: GetRoyaltyManagerExecutionRequestsOutput) => {
                    // Transform API response to match our UI data structure
                    const transformedRequests: IRoyaltyCalculationRequest[] = (response.executionRequests || []).map(
                        (request) => ({
                            executionRequestId: request.executionRequestId,
                            isAllEntities: request.isAllEntities,
                            isAllRegions: request.isAllRegions,
                            isAllServices: request.isAllServices,
                            requestDate:
                                request.requestDate instanceof Date
                                    ? request.requestDate.toISOString()
                                    : request.requestDate,
                            requestedBy: request.requestedBy,
                            statusName: request.statusName,
                            description: request.description,
                            periodFrom: request.periodFrom,
                            periodTo: request.periodTo,
                            priority: request.priority,
                            isMonthEnd: request.isMonthEnd || false
                        })
                    );

                    // Sort by requested date descending
                    const sortedRequests = orderBy(transformedRequests, ['requestDate'], ['desc']);
                    setRequestsRowData(sortedRequests);
                    setRequestsDataLoaded(true);
                },
                (error: UnknownCoralError) => {
                    console.error('Error loading royalty calculation requests:', error);

                    // Set empty data if API fails
                    setRequestsRowData([]);
                    setRequestsDataLoaded(true);

                    // Show error message to user
                    setTopAlertMsg(error.receivedException?.message || 'Failed to load royalty calculation requests.');
                }
            );
    }, [pathname, setTopAlertMsg]);

    const columnDefs: ColDef[] = useMemo(
        () => [
            {
                field: 'executionRequestId',
                headerName: 'Request ID',
                filter: 'agNumberColumnFilter',
                flex: 1,
                minWidth: 120,
                pinned: 'left'
            },
            {
                field: 'description',
                headerName: 'Description',
                filter: 'agTextColumnFilter',
                valueFormatter: emptyCellFormatter,
                flex: 3,
                minWidth: 200,
                pinned: 'left'
            },
            {
                field: 'statusName',
                headerName: 'Status',
                filter: 'agSetColumnFilter',
                cellRenderer: getStatusCellRenderer,
                flex: 1,
                minWidth: 100
            },
            {
                field: 'periodFrom',
                headerName: 'Period From',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                flex: 1,
                minWidth: 140
            },
            {
                field: 'periodTo',
                headerName: 'Period To',
                filter: 'agSetColumnFilter',
                valueFormatter: emptyCellFormatter,
                flex: 1,
                minWidth: 130
            },
            {
                field: 'requestedBy',
                headerName: 'Requested By',
                filter: 'agSetColumnFilter',
                flex: 1,
                minWidth: 150
            },
            {
                field: 'requestDate',
                headerName: 'Request Date',
                comparator: dateComparator,
                filter: 'agDateColumnFilter',
                filterParams: {
                    filterOptions: dateFilterOptions,
                    defaultOption: 'greaterThanOrEqual',
                    comparator: dateFilterComparator,
                    includeBlanksInLessThan: true,
                    includeBlanksInGreaterThan: true
                },
                cellClass: EXCEL_DATE_STYLE_ID,
                valueGetter: getYYYYMMDDUTCStrFromTimestamp,
                flex: 1,
                minWidth: 150
            },
            {
                field: 'isAllEntities',
                headerName: 'All Entities',
                filter: 'agSetColumnFilter',
                cellRenderer: getBooleanCellRenderer,
                flex: 1,
                minWidth: 140
            },
            {
                field: 'isAllRegions',
                headerName: 'All Regions',
                filter: 'agSetColumnFilter',
                cellRenderer: getBooleanCellRenderer,
                flex: 1,
                minWidth: 140
            },
            {
                field: 'isAllServices',
                headerName: 'All Services',
                filter: 'agSetColumnFilter',
                cellRenderer: getBooleanCellRenderer,
                flex: 1,
                minWidth: 140
            },
            {
                headerName: 'Actions',
                cellRenderer: (params: any) =>
                    getActionsCellRenderer({
                        ...params,
                        userInfo,
                        userRole,
                        handleViewRequest,
                        handleDetailRequest,
                        handleEditRequest,
                        handleDeleteRequest
                    }),
                sortable: false,
                filter: false,
                flex: 1,
                minWidth: 120,
                pinned: 'right'
            }
        ],
        [userInfo.username, userRole]
    );

    const setDefaultFilter = () => {
        // Show all statuses by default, but could be customized
        const statusFilterModel = {};
        agGridApi?.setFilterModel(statusFilterModel);
    };

    useEffect(() => {
        if (agGridApi) setDefaultFilter();
    }, [agGridApi]);

    // Calculate summary metrics
    const summaryMetrics = useMemo(() => {
        if (!requestsDataLoaded || !requestsRowData.length) {
            return {
                totalRequests: 0,
                completedRequests: 0,
                overdueRequests: 0,
                totalImpact: 0,
                avgInFlightTime: 0
            };
        }

        const totalRequests = requestsRowData.length;
        const completedRequests = requestsRowData.filter((req) => req.statusName === 'COMPLETED').length;
        const overdueRequests = requestsRowData.filter((req) => {
            // Calculate if request is overdue: not completed and more than 5 days old
            const requestDate = new Date(req.requestDate);
            const fiveDaysAgo = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000);
            return req.statusName !== 'COMPLETED' && requestDate < fiveDaysAgo;
        }).length;

        // Mock total impact calculation (in reality this would be calculated from actual royalty amounts)
        const totalImpact = requestsRowData.reduce((sum, req) => {
            if (req.statusName === 'COMPLETED') {
                return sum + 100000; // Mock calculation per completed request
            }
            return sum;
        }, 0);

        // Mock average processing time since we don't have duration in API response
        const avgInFlightTime = 2.5; // Mock 2.5 hours average

        return {
            totalRequests,
            completedRequests,
            overdueRequests,
            totalImpact,
            avgInFlightTime
        };
    }, [requestsDataLoaded, requestsRowData]);

    const exportToCSV = () => {
        const params = {
            fileName: `royalty-calculation-requests-${new Date().toISOString().split('T')[0]}.csv`
        };
        agGridApi?.exportDataAsCsv(params);
    };

    const handleCreateRequest = (requestData: any) => {
        // Convert date strings to Date objects for the API and set default priority
        const apiRequestData = {
            ...requestData,
            requestDate: new Date(requestData.requestDate),
            priority: 1 // Always default to high priority
        };

        contractConfigurationClient
            .createRoyaltyManagerExecutionRequest(CreateRoyaltyManagerExecutionRequestInput.clone(apiRequestData))
            .subscribe(
                (response: CreateRoyaltyManagerExecutionRequestOutput) => {
                    // Create a new request object for the UI using the API response structure
                    const newRequest: IRoyaltyCalculationRequest = {
                        executionRequestId: response.executionRequestId,
                        isAllEntities: requestData.isAllEntities || false,
                        isAllRegions: requestData.isAllRegions || false,
                        isAllServices: requestData.isAllServices || false,
                        isMonthEnd: requestData.isMonthEnd || false,
                        requestDate: requestData.requestDate,
                        requestedBy: requestData.requestedBy,
                        statusName: 'Pending',
                        description: requestData.description,
                        periodFrom: requestData.periodFrom,
                        periodTo: requestData.periodTo,
                        priority: 1 // Always default to high priority
                    };

                    // Add the new request to the existing data
                    setRequestsRowData((prev) => [newRequest, ...prev]);

                    // Show success toast message
                    setToasts([
                        {
                            id: `success-${Date.now()}`,
                            type: 'success',
                            message: `Royalty calculation request created successfully. Request ID: ${newRequest.executionRequestId}`
                        }
                    ]);
                },
                (error: UnknownCoralError) => {
                    console.error('Error creating royalty calculation request:', error);
                    setTopAlertMsg(
                        error.receivedException?.message ||
                            'Failed to create royalty calculation request. Please try again.'
                    );
                }
            );
    };

    const handleEditRequest = (request: IRoyaltyCalculationRequest) => {
        // Fetch detailed data before opening edit modal
        contractConfigurationClient
            .getRoyaltyManagerExecutionRequestDetailsById(
                GetRoyaltyManagerExecutionRequestDetailsByIdInput.clone({
                    executionRequestId: request.executionRequestId
                })
            )
            .subscribe(
                (response: any) => {
                    const details = (response as any).executionRequestDetails;

                    // Transform detailed API response to match modal's expected format
                    const detailedRequest = {
                        ...request,
                        // Map API response fields to modal format (arrays from API to expected field names)
                        serviceNames: details.services || [],
                        regionCodes: details.regions || [],
                        territoryCodes: details.territories || [],
                        entityNames: details.entities || [],
                        // Ensure boolean fields are properly set
                        isAllServices: details.isAllServices ?? request.isAllServices,
                        isAllRegions: details.isAllRegions ?? request.isAllRegions,
                        isAllEntities: details.isAllEntities ?? request.isAllEntities,
                        isMonthEnd: details.isMonthEnd ?? request.isMonthEnd
                    };

                    setSelectedRequest(detailedRequest);
                    setIsUpdateModalOpen(true);
                },
                (error: UnknownCoralError) => {
                    console.error('Error loading request details for edit:', error);
                    // Fallback to basic data if details API fails
                    setSelectedRequest(request);
                    setIsUpdateModalOpen(true);

                    setTopAlertMsg('Could not load all request details. Some fields may not be pre-populated.');
                }
            );
    };

    const handleDeleteRequest = (request: IRoyaltyCalculationRequest) => {
        setRequestToDelete(request);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteRequest = () => {
        if (!requestToDelete) return;

        // Call the actual delete API
        contractConfigurationClient
            .deleteRoyaltyManagerExecutionRequests(
                DeleteRoyaltyManagerExecutionRequestsInput.clone({
                    executionRequestIds: [requestToDelete.executionRequestId]
                })
            )
            .subscribe(
                (response: any) => {
                    // Handle batch response - check if our request was successfully deleted
                    if (
                        response.deletedRequestIds &&
                        response.deletedRequestIds.includes(requestToDelete.executionRequestId)
                    ) {
                        // Remove from local state
                        setRequestsRowData((prev) =>
                            prev.filter((r) => r.executionRequestId !== requestToDelete.executionRequestId)
                        );

                        setToasts([
                            {
                                id: `delete-success-${Date.now()}`,
                                type: 'success',
                                message: `Request ${requestToDelete.executionRequestId} deleted successfully.`
                            }
                        ]);
                    } else if (
                        response.notFoundRequestIds &&
                        response.notFoundRequestIds.includes(requestToDelete.executionRequestId)
                    ) {
                        setTopAlertMsg(
                            'Request could not be deleted. It may have already been processed or you may not have permission.'
                        );
                    } else if (
                        response.notPendingRequestIds &&
                        response.notPendingRequestIds.includes(requestToDelete.executionRequestId)
                    ) {
                        setTopAlertMsg('You are not authorized to delete this request.');
                    } else {
                        // Unexpected response - treat as error
                        setTopAlertMsg('Unexpected response from server. Please refresh and try again.');
                    }
                    setIsDeleteModalOpen(false);
                    setRequestToDelete(null);
                },
                (error: UnknownCoralError) => {
                    console.error('Error deleting royalty calculation request:', error);
                    console.error('Error details:', error.receivedException);

                    setTopAlertMsg(
                        error.receivedException?.message ||
                            'Failed to delete royalty calculation request. Please try again.'
                    );
                    setIsDeleteModalOpen(false);
                    setRequestToDelete(null);
                }
            );
    };

    const handleUpdateRequest = (requestData: any) => {
        if (!selectedRequest) return;

        // Prepare API request data
        const apiRequestData = {
            executionRequestId: selectedRequest.executionRequestId,
            requestDate: new Date(requestData.requestDate),
            requestedBy: requestData.requestedBy,
            description: requestData.description,
            periodFrom: requestData.periodFrom,
            periodTo: requestData.periodTo,
            priority: requestData.priority,
            isAllServices: requestData.isAllServices,
            serviceNames: requestData.isAllServices ? undefined : requestData.serviceNames,
            isAllRegions: requestData.isAllRegions,
            regionCodes: requestData.isAllRegions ? undefined : requestData.regionCodes,
            territoryCodes: requestData.territoryCodes?.length > 0 ? requestData.territoryCodes : undefined,
            isAllEntities: requestData.isAllEntities,
            entityNames: requestData.isAllEntities ? undefined : requestData.entityNames,
            isMonthEnd: requestData.isMonthEnd
        };

        contractConfigurationClient
            .updateRoyaltyManagerExecutionRequest(UpdateRoyaltyManagerExecutionRequestInput.clone(apiRequestData))
            .subscribe(
                (response: any) => {
                    // Update local state with the updated data
                    setRequestsRowData((prev) =>
                        prev.map((r) =>
                            r.executionRequestId === selectedRequest.executionRequestId ? { ...r, ...requestData } : r
                        )
                    );

                    setToasts([
                        {
                            id: `update-${Date.now()}`,
                            type: 'success',
                            message: `Request ${selectedRequest.executionRequestId} updated successfully.`
                        }
                    ]);

                    setSelectedRequest(null);
                    setIsUpdateModalOpen(false);
                },
                (error: UnknownCoralError) => {
                    console.error('Error updating royalty calculation request:', error);
                    setTopAlertMsg(
                        error.receivedException?.message ||
                            'Failed to update royalty calculation request. Please try again.'
                    );
                }
            );
    };

    const handleViewRequest = (requestId: number) => {
        setSelectedRequestId(requestId);
        setIsDetailsModalOpen(true);
    };

    const handleDetailRequest = (requestId: number) => {
        history.push(`${ROUTES.ROYALTY_MANAGER.INDEX}/${requestId}`);
    };

    const handleCloseDetailsModal = () => {
        setIsDetailsModalOpen(false);
        setSelectedRequestId(null);
    };

    // Wrapper functions to handle the different parameter types
    const handleEditFromModal = (requestId: number) => {
        const request = requestsRowData.find((r) => r.executionRequestId === requestId);
        if (request) {
            handleEditRequest(request);
        }
    };

    const handleDeleteFromModal = (requestId: number) => {
        const request = requestsRowData.find((r) => r.executionRequestId === requestId);
        if (request) {
            handleDeleteRequest(request);
        }
    };

    const requestsContent = (
        <Column className="royalty-manager-div" spacing="400" width="100%" height="100%">
            {/* Header with title and actions */}
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Column spacing="100">
                        <Text type="h100">Royalty Manager</Text>
                        <Text type="b200" color="secondary">
                            Track and manage royalty calculation execution requests
                        </Text>
                    </Column>
                    <Row spacing="200">
                        <Button type="link" size="medium" onClick={exportToCSV}>
                            <Icon tokens={downloadLargeTokens} />
                            Export
                        </Button>
                        <Button
                            type="primary"
                            size="medium"
                            disabled={!EDITOR_ROLE_SET.has(userRole)}
                            onClick={() => {
                                setIsCreateModalOpen(true);
                            }}
                        >
                            New Request
                        </Button>
                    </Row>
                </Row>
            </Box>

            {/* Summary Cards */}
            <Row spacing="300" width="100%">
                <Box type="outline" spacingInset="300" width="50%">
                    <Column spacing="100">
                        <Text type="b300" color="secondary">
                            Total Requests
                        </Text>
                        <Text type="h200">{summaryMetrics.totalRequests}</Text>
                        <Text type="b100" color="secondary">
                            {summaryMetrics.completedRequests} completed
                        </Text>
                    </Column>
                </Box>

                <Box type="outline" spacingInset="300" width="50%">
                    <Column spacing="100">
                        <Text type="b300" color="secondary">
                            Overdue Requests
                        </Text>
                        <Text type="h200">{summaryMetrics.overdueRequests}</Text>
                        <Text type="b100" color="secondary">
                            Require immediate attention
                        </Text>
                    </Column>
                </Box>
            </Row>

            {/* Search and Filter Controls */}
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <FilterInput filterText={filterText} setFilterText={setFilterText} />
                    <Row alignmentVertical="center">
                        <Button type="link" size="small">
                            Filters
                        </Button>
                    </Row>
                </Row>
            </Box>

            {/* Data Grid */}
            <div className="royalty-manager-grid">
                <BaseAgGrid
                    columnDefs={columnDefs}
                    rowData={requestsRowData}
                    filterText={filterText}
                    setAgGridApi={setAgGridApi}
                    onFirstDataRendered={setDefaultFilter}
                    loading={!requestsDataLoaded}
                />
            </div>
        </Column>
    );

    return (
        <div className="royalty-manager-container">
            {requestsContent}
            <CreateRoyaltyManagerRequestModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSubmit={handleCreateRequest}
            />
            <CreateRoyaltyManagerRequestModal
                isOpen={isUpdateModalOpen}
                onClose={() => {
                    setIsUpdateModalOpen(false);
                    setSelectedRequest(null);
                }}
                onSubmit={handleUpdateRequest}
                initialData={selectedRequest}
                isUpdate={true}
            />
            <RoyaltyManagerRequestDetailsModal
                isOpen={isDetailsModalOpen}
                onClose={handleCloseDetailsModal}
                requestId={selectedRequestId}
                onEdit={handleEditFromModal}
                onDelete={handleDeleteFromModal}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setRequestToDelete(null);
                }}
                title="Delete Request"
                body={`Are you sure you want to delete request ${requestToDelete?.executionRequestId}? This action cannot be undone.`}
                primaryButtonText="Delete"
                primaryButtonOnClick={confirmDeleteRequest}
                secondaryButtonText="Cancel"
                secondaryButtonOnClick={() => {
                    setIsDeleteModalOpen(false);
                    setRequestToDelete(null);
                }}
            />
        </div>
    );
};
